import { FC, useEffect } from 'react';

import { trackProductClickGA, trackSearchGA, trackSelectItemGA } from '@telialt/gtm-tracking/src/events';
import { useHybrisSegment } from '@telialt/utils/src/segment';

import { SEARCH_QUERY_MIN_LENGTH } from '../../constants';
import { useLoadQueryKeys } from '../../hooks/useLoadQueryKeys';
import { useLoadSearchSuggestions } from '../../hooks/useLoadSearchSuggestions';
import {
    ContentSuggestionClickHandler,
    HelpSuggestionClickHandler,
    ProductSuggestionClickHandler,
    SearchSuggestionClickHandler,
    ServiceSuggestionClickHandler,
    SuggestionClickCallbacks,
} from '../../model';
import { SEARCH_TEST_ID } from '../../test/test-ids';
import { handleLupaSearchEvent } from '../../utils/LupaSearchTracking';
import { getQueryKeyPrefixBySegment } from '../../utils/segment';
import { NoSuggestions } from './NoSuggestions/NoSuggestions';
import { ResultSuggestions, SuggestionsWrapper } from './SearchSuggestions.styles';
import { AllSuggestionsSkeleton } from './Skeletons/AllSuggestionsSkeleton';
import { AutoComplete } from './Suggestions/AutoComplete';
import { ContentSuggestions } from './Suggestions/ContentSuggestions';
import { HelpSuggestions } from './Suggestions/HelpSuggestions';
import { PopularQueries } from './Suggestions/PopularQueries';
import { ProductsSuggestions } from './Suggestions/ProductsSuggestions';
import { ServicesSuggestions } from './Suggestions/ServicesSuggestions';
import { SpecialOffersSuggestions } from './Suggestions/SpecialOffersSuggestions';
import { SuggestionsError } from './SuggestionsError';

type Props = {
    keyword: string;
    isNavBar?: boolean;
    callbacks?: SuggestionClickCallbacks;
};

export const SearchSuggestions: FC<Props> = ({ keyword, isNavBar = false, callbacks }) => {
    const segment = useHybrisSegment();
    const queryKeyPrefix = getQueryKeyPrefixBySegment(segment);

    const { isLoading: isQueryKeysLoading, queryKeys } = useLoadQueryKeys();
    const { suggestions, products, services, help, content, searchText, isLoading, error } =
        useLoadSearchSuggestions(keyword);

    useEffect(() => {
        if (searchText && products) {
            trackSearchGA({ source: 'query', data: { searchText, products: products.items } });
        }
    }, [products, searchText]);

    if (isQueryKeysLoading || isLoading) {
        return (
            <div aria-live="polite" aria-busy="true">
                <AllSuggestionsSkeleton isNavBar={isNavBar} />
            </div>
        );
    }

    if (error && keyword.length >= SEARCH_QUERY_MIN_LENGTH) {
        return (
            <div aria-live="polite" aria-busy="false">
                <SuggestionsError />
            </div>
        );
    }

    const hasNoResults = !products?.total && !services?.total && !help?.total && !content?.total;

    if (hasNoResults && !searchText && !suggestions?.length) {
        return null;
    }

    if (hasNoResults && !!searchText) {
        return (
            <SuggestionsWrapper
                data-test={SEARCH_TEST_ID.SUGGESTIONS_NO_RESULTS}
                $isNavBar={isNavBar}
                $isNoResults={true}
                aria-live="polite"
                aria-busy="false"
            >
                <NoSuggestions keyword={searchText} isNavBar={isNavBar} />
            </SuggestionsWrapper>
        );
    }

    const {
        onAutoCompleteSuggestionClick,
        onProductSuggestionClick,
        onServiceSuggestionClick,
        onHelpSuggestionClick,
        onContentSuggestionClick,
    } = callbacks || {};

    const handleSuggestionClick: SearchSuggestionClickHandler = async (event, { suggestion, queryKey }) => {
        if (onAutoCompleteSuggestionClick) {
            event.preventDefault();
            onAutoCompleteSuggestionClick(suggestion, event);
        }

        trackSearchGA({
            source: 'autocompleteSuggestionClick',
            data: { searchText: searchText || '', suggestionText: suggestion },
        });
        await handleLupaSearchEvent('suggestionClick', queryKey, searchText || '', suggestion);
    };

    const handleProductClick: ProductSuggestionClickHandler = async (product, event) => {
        onProductSuggestionClick?.(product, event);
        if (searchText) {
            trackProductClickGA({ source: 'autocomplete', data: { searchText, product } });
        } else {
            trackProductClickGA({ source: 'recommendations', data: { product } });
        }

        trackSelectItemGA({
            source: 'searchProducts',
            data: { searchText: searchText || '', products: [product], itemListName: 'search-autocomplete' },
        });

        await handleLupaSearchEvent('itemClick', queryKeys[`${queryKeyPrefix}-products`], searchText || '', product.id);
    };

    const handleServiceClick: ServiceSuggestionClickHandler = async (service, event) => {
        onServiceSuggestionClick?.(service, event);
        trackSearchGA({
            source: 'autocompleteSuggestionClick',
            data: { searchText: searchText || '', suggestionText: service.title },
        });
        await handleLupaSearchEvent('itemClick', queryKeys[`${queryKeyPrefix}-services`], searchText || '', service.id);
    };

    const handleHelpClick: HelpSuggestionClickHandler = async (helpItem, event) => {
        onHelpSuggestionClick?.(helpItem, event);
        trackSearchGA({
            source: 'autocompleteSuggestionClick',
            data: { searchText: searchText || '', suggestionText: helpItem.title },
        });
        await handleLupaSearchEvent('itemClick', queryKeys[`${queryKeyPrefix}-help`], searchText || '', helpItem.id);
    };

    const handleContentClick: ContentSuggestionClickHandler = async (contentItem, event) => {
        onContentSuggestionClick?.(contentItem, event);
        trackSearchGA({
            source: 'autocompleteSuggestionClick',
            data: { searchText: searchText || '', suggestionText: contentItem.title },
        });
        await handleLupaSearchEvent('itemClick', queryKeys[`${queryKeyPrefix}-help`], searchText || '', contentItem.id);
    };

    return (
        <SuggestionsWrapper data-test={SEARCH_TEST_ID.SUGGESTIONS_DROPDOWN_CONTENT} $isNavBar={isNavBar}>
            {!searchText || searchText.length < SEARCH_QUERY_MIN_LENGTH ? (
                <PopularQueries suggestions={suggestions || []} onClick={handleSuggestionClick} />
            ) : (
                <AutoComplete suggestions={suggestions || []} searchText={searchText} onClick={handleSuggestionClick} />
            )}
            <ResultSuggestions>
                {!searchText && (!!products?.items.length || !!services?.items.length) && (
                    <SpecialOffersSuggestions
                        products={products?.items || []}
                        services={services?.items || []}
                        onProductClick={handleProductClick}
                        onServiceClick={handleServiceClick}
                    />
                )}
                {!!searchText && !!products?.items.length && (
                    <ProductsSuggestions products={products.items} onClick={handleProductClick} />
                )}
                {!!searchText && !!services?.items.length && (
                    <ServicesSuggestions services={services.items} onClick={handleServiceClick} />
                )}
                {!!searchText && !!help?.items.length && (
                    <HelpSuggestions helpItems={help.items} onClick={handleHelpClick} />
                )}
                {!!searchText && !!content?.items.length && (
                    <ContentSuggestions contentItems={content.items} onClick={handleContentClick} />
                )}
            </ResultSuggestions>
        </SuggestionsWrapper>
    );
};
